import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useGlobals } from 'src/shared/contexts/appContext';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { replaceHelpPageTermsInText } from 'src/modules/support/helpArticles/functions/helpPageTerms';
import { HelpCenterCategory } from '../models/helpCenterCategory';
import { HelpCenterArticleListItem } from '../models/helpCenterArticleListItem';
import { getHelpCenterArticles, getHelpCenterCategories } from '../services/helpCenterService';
import { groupHelpCenterCategories } from '../functions/groupHelpCenterCategories';
import { HelpCenterLayout } from '../components/helpCenterLayout';
import { getHelpCenterTrail } from '../functions/getHelpCenterTrail';

export const HelpCategoryPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { currentUser } = useGlobals();
  const categoryId = Number(id);
  const [categories, setCategories] = useState<HelpCenterCategory[]>([]);
  const [articles, setArticles] = useState<HelpCenterArticleListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<'notFound' | 'failed' | null>(null);

  // En okänd eller dold kategori svarar 404 utan toast och visas som en text med väg tillbaka. Andra fel ger en egen
  // text, eftersom artikelanropet inte visar någon toast. cancelled gör att ett sent svar för en kategori man redan
  // lämnat i spalten inte skriver över den nya.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      setArticles([]);
      try {
        const [categoryResult, articleResult] = await Promise.all([getHelpCenterCategories(), getHelpCenterArticles(categoryId)]);
        if (!cancelled) {
          setCategories(categoryResult.data);
          setArticles(articleResult.data);
        }
      } catch (e) {
        ConsoleHelper.log(e);
        if (!cancelled) setError((e as { status?: number })?.status === 404 ? 'notFound' : 'failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [categoryId]);

  const category = categories.find((x) => x.id === categoryId);
  const section = useMemo(
    () => groupHelpCenterCategories(categories).find((x) => x.cards.some((card) => card.id === categoryId)),
    [categories, categoryId],
  );

  // En huvudkategori utan egna artiklar finns i kategorierna men har ingen sida; den räknas som att den inte finns.
  const found = !error && !!category && !!section && articles.length > 0;

  return (
    <HelpCenterLayout trail={getHelpCenterTrail(section, category)} section={section} currentCategoryId={categoryId}>
      <Box sx={{ minHeight: loading ? '10rem' : undefined }}>
        <LoadingOverlay loading={loading} />
        {!loading && !found && (
          <Box sx={{ mt: 2 }}>
            <Typography color='text.secondary'>{t(error === 'failed' ? 'help:loadFailed' : 'help:categoryNotFound')}</Typography>
            <MuiLink component={Link} to={RoutesEnum.Help} sx={{ display: 'inline-block', mt: 1 }}>
              {t('help:backToHelp')}
            </MuiLink>
          </Box>
        )}
        {!loading && found && category && (
          <>
            <MainContentHeader title={category.name} />
            {category.description && (
              <Typography color='text.secondary' sx={{ mt: 1, whiteSpace: 'pre-line' }}>
                {replaceHelpPageTermsInText(category.description, currentUser?.mainSettings ?? [])}
              </Typography>
            )}
            <List>
              {articles.map((article) => (
                <ListItemButton key={article.slug} component={Link} to={`${RoutesEnum.HelpArticle}/${encodeURIComponent(article.slug)}`} divider>
                  <ListItemText primary={article.title} />
                </ListItemButton>
              ))}
            </List>
          </>
        )}
      </Box>
    </HelpCenterLayout>
  );
};
